import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useEnvStore } from "@/os/env/useEnvStore";
import { sandboxIdentityForPath } from "@/auth/sandboxUser";
import { useSandboxProfileOverrides, applyOverrides } from "@/auth/sandboxProfileOverrides";
import { useOSThemeStore } from "@/os/theme/useOSTheme";
import { useI18n } from "@/i18n/I18nProvider";
import { preloadOS } from "@/os/preloadOS";
import scIconDark from "@/assets/sc-icon-dark.png";
import scIconLight from "@/assets/sc-icon-light.png";
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Users2,
  FileText,
  Receipt,
  CreditCard,
  Building2,
  LogOut,
  Shield,
  Wrench,
  UserRound,
  ChevronsUpDown,
  Check,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  FlaskConical,
  ShieldCheck,
  Repeat,
  Boxes,
  Menu,
  Hammer,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { AppRole } from "@/auth/AuthProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PersonAvatar } from "@/os/ui";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { CommandPalette, CommandPaletteHint } from "@/os/components/CommandPalette";
import { GlobalTimerIndicator } from "@/os/components/GlobalTimerIndicator";
import { OSBootSplash } from "@/os/components/OSBootSplash";
import { OSErrorBoundary } from "@/os/components/OSErrorBoundary";
import { useAdminInsights } from "@/os/hooks/useAdminInsights";
import { useClientUnseenQuotes } from "@/os/hooks/useClientUnseenQuotes";
import { useImpersonation } from "@/os/impersonation/ImpersonationProvider";
import {
  useClientImpersonation,
  endClientImpersonation,
  clearClientImpersonation,
} from "@/os/impersonation/clientImpersonation";
import { UserCog, X, ActivitySquare } from "lucide-react";
import { setMonitoringTag } from "@/monitoring/sentry";

type Item = { to: string; label: string; icon: any; alertKey?: "invoices" | "projects" | "quotes" | "clients" | "client_quotes" };
type NavGroup = { label?: string; items: Item[] };

export function OSLayout({ role }: { role: AppRole }) {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const env = useEnvStore((s) => s.env);
  const clearEnv = useEnvStore((s) => s.clearEnv);
  const isSandbox = env === "sandbox";
  const { t, lang } = useI18n();

  const g = (t as any).os.nav.groups ?? {};
  const adminNav: NavGroup[] = [
    { items: [{ to: "/os/admin", label: t.os.nav.dashboard, icon: LayoutDashboard }] },
    {
      label: g.commercial,
      items: [
        { to: "/os/admin/clients", label: t.os.nav.clients, icon: Building2, alertKey: "clients" },
        { to: "/os/admin/projects", label: t.os.nav.projects, icon: Briefcase, alertKey: "projects" },
        { to: "/os/admin/quotes", label: t.os.nav.quotes, icon: FileText, alertKey: "quotes" },
        { to: "/os/admin/invoices", label: t.os.nav.invoices, icon: Receipt, alertKey: "invoices" },
      ],
    },
    {
      label: g.operations,
      items: [
        { to: "/os/admin/team", label: t.os.nav.team, icon: Users2 },
        { to: "/os/admin/studio", label: (t as any).os.nav.studio ?? "Studio", icon: Hammer },
        { to: "/os/admin/resources", label: (t as any).os.nav.resources, icon: Boxes },
      ],
    },
    {
      label: g.administration,
      items: [
        { to: "/os/admin/users", label: t.os.nav.users, icon: Users },
        { to: "/os/admin/monitoring", label: lang === "es" ? "Monitoreo" : "Monitoring", icon: ActivitySquare },
        { to: "/os/admin/settings", label: (t as any).os.nav.settings ?? "Settings", icon: Settings },
      ],
    },
  ];
  const operatorNav: NavGroup[] = [
    { items: [{ to: "/os/operator", label: t.os.nav.dashboard, icon: LayoutDashboard }] },
    {
      label: g.workspace,
      items: [
        { to: "/os/operator/work-studio", label: (t as any).os.nav.workStudio ?? "Work Studio", icon: Hammer },
        { to: "/os/operator/projects", label: t.os.nav.myProjects, icon: Briefcase },
      ],
    },
  ];
  const clientNav: NavGroup[] = [
    { items: [{ to: "/os/client", label: t.os.nav.dashboard, icon: LayoutDashboard }] },
    {
      label: g.workspace,
      items: [
        { to: "/os/client/projects", label: t.os.nav.myProjects, icon: Briefcase },
        { to: "/os/client/invoices", label: t.os.nav.invoices, icon: Receipt },
        { to: "/os/client/quotes", label: t.os.nav.quotes, icon: FileText, alertKey: "client_quotes" },
      ],
    },
  ];
  const hubMeta: Record<AppRole, { label: string; icon: any }> = {
    admin: { label: t.os.hubs.admin.label, icon: Shield },
    operator: { label: t.os.hubs.operator.label, icon: Wrench },
    client: { label: t.os.hubs.client.label, icon: UserRound },
    prospect: { label: t.os.hubs.prospect.label, icon: UserRound },
  };
  const hubSwitchOptions: { role: AppRole; to: string; label: string; icon: any; desc: string }[] = [
    { role: "admin", to: "/os/admin", label: t.os.hubs.admin.label, icon: Shield, desc: t.os.hubs.admin.desc },
    { role: "operator", to: "/os/operator", label: t.os.hubs.operator.label, icon: Wrench, desc: t.os.hubs.operator.desc },
    { role: "client", to: "/os/client", label: t.os.hubs.client.label, icon: UserRound, desc: t.os.hubs.client.desc },
  ];

  const [profile, setProfile] = useState<{
    full_name: string | null;
    company: string | null;
    avatar_url: string | null;
    avatar_color: string | null;
  } | null>(null);
  const sandboxOverrides = useSandboxProfileOverrides((s) => s.byId);

  useEffect(() => {
    // In sandbox we never hit Lovable Cloud for profile data.
    if (isSandbox) {
      const id = sandboxIdentityForPath(loc.pathname);
      const p = applyOverrides(id.id, id.profile);
      setProfile({
        full_name: p.full_name,
        company: p.company,
        avatar_url: p.avatar_url,
        avatar_color: p.avatar_color,
      });
      return;
    }
    if (!user) {
      setProfile(null);
      return;
    }
    supabase
      .from("profiles")
      .select("full_name, company, avatar_url, avatar_color")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => setProfile((data as any) ?? null));
  }, [user, isSandbox, loc.pathname, sandboxOverrides]);

  const displayName =
    profile?.full_name?.trim() || user?.email?.split("@")[0] || t.os.hubs.accountSettings;
  const displayCompany = profile?.company?.trim() || "";
  const initials = displayName
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleSignOut = async () => {
    await clearClientImpersonation();
    if (!isSandbox) await signOut();
    clearEnv();
    nav("/");
  };

  const handleSwitchEnv = () => {
    clearEnv();
    nav("/os/select");
  };

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("os-sidebar-collapsed") === "true";
  });
  useEffect(() => {
    localStorage.setItem("os-sidebar-collapsed", String(collapsed));
  }, [collapsed]);

  // Pre-load every OS route chunk once the user is inside the backoffice so
  // subsequent navigation (Dashboard → Projects → Studio …) is instant and
  // never shows a loading state again.
  useEffect(() => {
    preloadOS();
  }, []);

  const [mobileOpen, setMobileOpen] = useState(false);

  // OS theme — synced with user preference (defaults to dark per Vercel spec).
  const osTheme = useOSThemeStore((s) => s.theme);
  const osDark = osTheme === "dark";
  const sidebarIconSrc = osDark ? scIconDark : scIconLight;

  // Radix portals (Dropdown, Dialog, Select, Popover, Toast…) render to <body>,
  // outside the .os-theme wrapper. Mirror the theme classes on <body> while the
  // OS is mounted so portaled content inherits the same tokens.
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const prevHtmlDark = html.classList.contains("dark");
    body.classList.add("os-theme");
    if (osDark) {
      body.classList.add("dark");
      html.classList.add("dark");
    } else {
      body.classList.remove("dark");
      html.classList.remove("dark");
    }
    return () => {
      body.classList.remove("os-theme");
      body.classList.remove("dark");
      // Restore the guest-hub html theme on unmount.
      if (prevHtmlDark) html.classList.add("dark");
      else html.classList.remove("dark");
    };
  }, [osDark]);

  const groups =
    role === "admin" ? adminNav : role === "operator" ? operatorNav : clientNav;
  // Background insights — drives badge counts on admin nav items.
  const { data: insights } = useAdminInsights(role === "admin");
  const adminAlertCounts = insights?.alertCounts;
  const clientQuotesUnseen = useClientUnseenQuotes(role === "client");
  const alertCounts = {
    ...(adminAlertCounts ?? {}),
    client_quotes: clientQuotesUnseen,
  } as Record<string, number>;
  const meta = hubMeta[role];
  const HubIcon = meta.icon;

  // ───── Impersonation: banner + auto-stop when leaving the project ─────
  const { active: impersonation, stop: stopImpersonation } = useImpersonation();
  useEffect(() => {
    if (!impersonation) return;
    const onProject = loc.pathname.includes(`/projects/${impersonation.projectId}`);
    if (!onProject) stopImpersonation();
  }, [loc.pathname, impersonation, stopImpersonation]);
  // ───── Impersonación de cliente (sesión real, solo super admin) ─────
  const clientImpersonation = useClientImpersonation((s) => s.active);
  useEffect(() => {
    // Marca la sesión en Sentry para no contaminar métricas de usuarios reales.
    setMonitoringTag("impersonating", clientImpersonation ? "true" : undefined);
  }, [clientImpersonation]);
  const [endingImpersonation, setEndingImpersonation] = useState(false);
  const handleEndClientImpersonation = async () => {
    setEndingImpersonation(true);
    try {
      await endClientImpersonation();
    } finally {
      setEndingImpersonation(false);
    }
  };

  const handleEndImpersonation = () => {
    stopImpersonation();
    nav("/os/admin/projects");
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className={cn("os-theme h-screen overflow-hidden bg-background text-foreground flex font-sans", osDark && "dark")}>
        <OSBootSplash role={role} userId={user?.id ?? null} />
        <CommandPalette role={role} />
        {/* Sidebar */}
        <aside
          className={cn(
            "hidden lg:flex shrink-0 flex-col border-r bg-background transition-all duration-200 h-screen sticky top-0",
            collapsed ? "w-16" : "w-64"
          )}
        >
          <div className={cn("h-16 flex items-center border-b", collapsed ? "justify-center px-2" : "px-3")}>
            {isSandbox ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                className={cn(
                  "flex items-center gap-2 rounded-md py-1.5 hover:bg-foreground/[0.04] transition-colors outline-none",
                  collapsed ? "justify-center px-1.5" : "w-full px-2"
                )}
              >
                <img
                  src={sidebarIconSrc}
                  alt="Smart Canvas"
                  className="h-7 w-7 rounded-md object-contain shrink-0"
                />
                {!collapsed && (
                  <>
                    <div className="flex flex-col leading-tight text-left flex-1 min-w-0">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-foreground">
                        {t.os.hubs.smartCanvasOs}
                      </span>
                      <span className="text-[10px] font-medium truncate text-muted-foreground/60">
                        {meta.label}
                      </span>
                    </div>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  </>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-normal">
                  {t.os.hubs.switch}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {hubSwitchOptions.map((opt) => {
                  const Icon = opt.icon;
                  const current = opt.role === role;
                  return (
                    <DropdownMenuItem
                      key={opt.role}
                      onClick={() => nav(opt.to)}
                      className="gap-2 cursor-pointer"
                    >
                      <Icon className="h-4 w-4" />
                      <div className="flex flex-col flex-1">
                        <span className="text-sm">{opt.label}</span>
                        <span className="text-[11px] text-muted-foreground">{opt.desc}</span>
                      </div>
                      {current && <Check className="h-3.5 w-3.5" />}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            ) : (
              <div
                className={cn(
                  "flex items-center gap-2 py-1.5",
                  collapsed ? "justify-center px-1.5" : "w-full px-2"
                )}
              >
                <img
                  src={sidebarIconSrc}
                  alt="Smart Canvas"
                  className="h-7 w-7 rounded-md object-contain shrink-0"
                />
                {!collapsed && (
                  <div className="flex flex-col leading-tight text-left flex-1 min-w-0">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-foreground">
                      {t.os.hubs.smartCanvasOs}
                    </span>
                    <span className="text-[10px] font-medium truncate text-muted-foreground/60">
                      {meta.label}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <nav className={cn("flex-1 overflow-y-auto", collapsed ? "p-2 space-y-1" : "p-3 space-y-4")}>
            {groups.map((group, gi) => (
              <div key={gi} className="space-y-0.5">
                {!collapsed && group.label && (
                  <div className="px-3 pt-1 pb-1 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground/70">
                    {group.label}
                  </div>
                )}
                {collapsed && group.label && gi > 0 && (
                  <div className="mx-2 my-1 h-px bg-border/60" />
                )}
                {group.items.map((it) => {
                  const Icon = it.icon;
                  const isHubRoot = it.to.split("/").length === 3;
                  const active = isHubRoot
                    ? loc.pathname === it.to
                    : loc.pathname === it.to || loc.pathname.startsWith(it.to + "/");
                  const count = it.alertKey && alertCounts ? alertCounts[it.alertKey] : 0;
                  const link = (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={isHubRoot}
                      className={cn(
                        "flex items-center rounded-md transition-colors",
                        collapsed
                          ? "justify-center px-2 py-2.5"
                          : "gap-2.5 px-3 py-2 text-sm",
                        active
                          ? "bg-foreground/[0.06] text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03]",
                      )}
                    >
                      <span className="relative inline-flex">
                        <Icon className="h-4 w-4" />
                        {collapsed && count > 0 && (
                          <span className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full bg-rose-500" />
                        )}
                      </span>
                      {!collapsed && <span className="flex-1">{it.label}</span>}
                      {!collapsed && count > 0 && (
                        <span
                          className={cn(
                            "shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium tabular-nums",
                            "bg-rose-500/15 text-rose-600 dark:text-rose-400",
                          )}
                        >
                          {count > 99 ? "99+" : count}
                        </span>
                      )}
                    </NavLink>
                  );

                  if (collapsed) {
                    return (
                      <Tooltip key={it.to}>
                        <TooltipTrigger asChild>{link}</TooltipTrigger>
                        <TooltipContent side="right">
                          {it.label}
                          {count > 0 && (
                            <span className="ml-1.5 text-rose-500">· {count}</span>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }
                  return link;
                })}
              </div>
            ))}
          </nav>

          {/* Sidebar footer */}
          <div className="border-t p-2 space-y-1">
            {role === "operator" && <GlobalTimerIndicator collapsed={collapsed} />}
            {/* Account */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={cn(
                  "flex items-center gap-2 rounded-md py-1.5 outline-none transition-colors hover:bg-foreground/[0.04] w-full",
                  collapsed ? "justify-center px-1" : "px-2"
                )}
              >
                <PersonAvatar src={profile?.avatar_url} name={displayName} size="sm" color={profile?.avatar_color} />
                {!collapsed && (
                  <>
                    <div className="flex flex-col leading-tight text-left flex-1 min-w-0">
                      <span className="text-xs font-medium truncate">{displayName}</span>
                      <span className="text-[11px] text-muted-foreground truncate">
                        {displayCompany || user?.email}
                      </span>
                    </div>
                    <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  </>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="top"
                className="w-60"
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-medium truncate">{displayName}</span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {displayCompany || user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => nav(`/os/${role}/account`)}
                  className="gap-2 cursor-pointer"
                >
                  <Settings className="h-4 w-4" />
                  {t.os.hubs.accountSettings}
                </DropdownMenuItem>
                {isSandbox && (
                  <DropdownMenuItem
                    onClick={handleSwitchEnv}
                    className="gap-2 cursor-pointer"
                  >
                    <Repeat className="h-4 w-4" />
                    {t.os.hubs.switchEnv}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="gap-2 cursor-pointer"
                >
                  <LogOut className="h-4 w-4" />
                  {t.os.common.signOut}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Collapse toggle */}
            <div className={cn("flex items-center pt-1 border-t border-border/60", collapsed ? "justify-center" : "justify-between")}>
              <button
                onClick={() => setCollapsed((v) => !v)}
                className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04] transition-colors mt-1"
                aria-label={collapsed ? t.os.hubs.expand : t.os.hubs.collapse}
              >
                {collapsed ? (
                  <PanelLeftOpen className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </button>
              {!collapsed && isSandbox && (
                <span
                  className={cn(
                    "shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] border mt-1",
                    "border-amber-500/40 text-amber-500/90 bg-amber-500/[0.06]",
                  )}
                >
                  <FlaskConical className="h-2.5 w-2.5" />
                </span>
              )}
            </div>
          </div>
        </aside>

        {/* Mobile topbar */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 h-screen overflow-hidden">
          <div className="lg:hidden sticky top-0 z-30 h-14 flex items-center justify-between px-4 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
            <div className="flex items-center gap-3">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04] transition-colors"
                    aria-label={t.nav.menu}
                  >
                    <Menu className="h-5 w-5" />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
                  <SheetTitle className="sr-only">{meta.label}</SheetTitle>
                  <div className="h-14 flex items-center gap-2 px-4 border-b">
                    <div className="h-6 w-6 rounded bg-foreground text-background flex items-center justify-center text-[9px] font-bold">SC</div>
                    <span className="text-xs font-medium">{meta.label}</span>
                  </div>
                  <nav className="flex-1 overflow-y-auto p-3 space-y-4">
                    {groups.map((group, gi) => (
                      <div key={gi} className="space-y-0.5">
                        {group.label && (
                          <div className="px-3 pt-1 pb-1 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground/70">
                            {group.label}
                          </div>
                        )}
                        {group.items.map((it) => {
                          const Icon = it.icon;
                          const isHubRoot = it.to.split("/").length === 3;
                          const active = isHubRoot
                            ? loc.pathname === it.to
                            : loc.pathname === it.to || loc.pathname.startsWith(it.to + "/");
                          const count = it.alertKey && alertCounts ? alertCounts[it.alertKey] : 0;
                          return (
                            <NavLink
                              key={it.to}
                              to={it.to}
                              end={isHubRoot}
                              onClick={() => setMobileOpen(false)}
                              className={cn(
                                "flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors",
                                active
                                  ? "bg-foreground/[0.06] text-foreground font-medium"
                                  : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03]",
                              )}
                            >
                              <span className="relative inline-flex">
                                <Icon className="h-4 w-4" />
                              </span>
                              <span className="flex-1">{it.label}</span>
                              {count > 0 && (
                                <span className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium tabular-nums bg-rose-500/15 text-rose-600 dark:text-rose-400">
                                  {count > 99 ? "99+" : count}
                                </span>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    ))}
                  </nav>
                  {role === "operator" && (
                    <div className="border-t p-3" onClick={() => setMobileOpen(false)}>
                      <GlobalTimerIndicator collapsed={false} />
                    </div>
                  )}
                </SheetContent>
              </Sheet>
              <div className="flex items-center gap-2">
                <img
                  src={sidebarIconSrc}
                  alt="Smart Canvas"
                  className="h-6 w-6 rounded object-contain"
                />
                <span className="text-xs font-medium">{meta.label}</span>
              </div>
            </div>
            {/* Account avatar (replaces the old logout button — logout lives in the menu). */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className="inline-flex h-10 w-10 items-center justify-center rounded-md outline-none hover:bg-foreground/[0.04] transition-colors"
                aria-label={t.os.hubs.accountSettings}
              >
                <PersonAvatar src={profile?.avatar_url} name={displayName} size="sm" color={profile?.avatar_color} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-medium truncate">{displayName}</span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {displayCompany || user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav(`/os/${role}/account`)} className="gap-2 cursor-pointer">
                  <Settings className="h-4 w-4" />
                  {t.os.hubs.accountSettings}
                </DropdownMenuItem>
                {isSandbox && (
                  <DropdownMenuItem onClick={handleSwitchEnv} className="gap-2 cursor-pointer">
                    <Repeat className="h-4 w-4" />
                    {t.os.hubs.switchEnv}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="gap-2 cursor-pointer">
                  <LogOut className="h-4 w-4" />
                  {t.os.common.signOut}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <main className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto bg-background">
            {clientImpersonation && (
              <div className="sticky top-0 z-50 border-b border-amber-500/40 bg-amber-500/[0.14] backdrop-blur">
                <div className="flex items-center gap-3 px-4 py-2 text-xs">
                  <UserCog className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                  <div className="flex-1 min-w-0 truncate">
                    {lang === "es" ? "Estás impersonando a " : "You are impersonating "}
                    <span className="font-medium text-foreground">
                      {clientImpersonation.companyName || clientImpersonation.email}
                    </span>
                    {clientImpersonation.companyName && (
                      <span className="text-muted-foreground"> · {clientImpersonation.email}</span>
                    )}
                  </div>
                  <button
                    onClick={handleEndClientImpersonation}
                    disabled={endingImpersonation}
                    className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-background/60 px-2 py-1 text-[11px] font-medium text-foreground hover:bg-background transition-colors disabled:opacity-60"
                  >
                    <X className="h-3 w-3" />
                    {lang === "es" ? "Finalizar" : "End"}
                  </button>
                </div>
              </div>
            )}
            {impersonation && (
              <div className="sticky top-0 z-40 border-b bg-amber-500/[0.08] backdrop-blur supports-[backdrop-filter]:bg-amber-500/[0.10]">
                <div className="flex items-center gap-3 px-4 py-2 text-xs">
                  <UserCog className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                  <div className="flex-1 min-w-0 truncate">
                    Estás viendo el proyecto{" "}
                    <span className="font-medium text-foreground">{impersonation.projectName}</span>{" "}
                    como{" "}
                    <span className="font-medium text-foreground">{impersonation.name}</span>
                    <span className="text-muted-foreground">
                      {" "}· {impersonation.role === "client" ? "Equipo del Cliente" : "Equipo de Smart Canvas"}
                      {impersonation.role_on_project ? ` · ${impersonation.role_on_project}` : ""}
                    </span>
                  </div>
                  <button
                    onClick={handleEndImpersonation}
                    className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-background/60 px-2 py-1 text-[11px] font-medium text-foreground hover:bg-background transition-colors"
                  >
                    <X className="h-3 w-3" />
                    Finalizar impersonalización
                  </button>
                </div>
              </div>
            )}
            <motion.div
              key={loc.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: [0.25, 1, 0.5, 1] }}
              className="h-full min-h-full bg-background flex flex-col"
            >
              <OSErrorBoundary key={loc.pathname}>
                <Outlet />
              </OSErrorBoundary>
            </motion.div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
